<template>

  <!-- ══════════════════════════════════════════
       DESKTOP HERO  (hidden on mobile ≤768px)
  ══════════════════════════════════════════ -->
  <section class="hero">
    <!-- Animated gradient blobs -->
    <div class="hero-bg" aria-hidden="true">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
      <div class="blob blob-4"></div>
    </div>

    <!-- Floating chemistry symbols -->
    <div class="math-symbols" aria-hidden="true">
      <span class="ms ms-1">⚗</span>
      <span class="ms ms-2">⬡</span>
      <span class="ms ms-3">H₂O</span>
      <span class="ms ms-4">⚛</span>
      <span class="ms ms-5">CO₂</span>
      <span class="ms ms-6">⬡</span>
      <span class="ms ms-7">Δ</span>
      <span class="ms ms-8">NaCl</span>
      <span class="ms ms-9">🧪</span>
      <span class="ms ms-10">O₂</span>
      <span class="ms ms-11">⬡</span>
      <span class="ms ms-12">pH</span>
      <span class="ms ms-13">⚛</span>
      <span class="ms ms-14">CH₄</span>
    </div>

    <!-- Molecular line pattern (very low opacity brand texture) -->
    <svg class="molecule-pattern" aria-hidden="true" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
      <g fill="none" stroke="#F7B424" stroke-width="1.4">
        <line x1="60" y1="90" x2="180" y2="150" />
        <line x1="180" y1="150" x2="180" y2="270" />
        <line x1="180" y1="150" x2="300" y2="90" />
        <line x1="620" y1="480" x2="740" y2="420" />
        <line x1="620" y1="480" x2="620" y2="360" />
        <line x1="500" y1="540" x2="620" y2="480" />
      </g>
      <g fill="#F7B424">
        <circle cx="60" cy="90" r="5" />
        <circle cx="180" cy="150" r="6" />
        <circle cx="180" cy="270" r="5" />
        <circle cx="300" cy="90" r="5" />
        <circle cx="620" cy="480" r="6" />
        <circle cx="740" cy="420" r="5" />
        <circle cx="620" cy="360" r="5" />
        <circle cx="500" cy="540" r="5" />
      </g>
      <g fill="none" stroke="#ffffff" stroke-width="1.5">
        <polygon points="400,40 434,60 434,100 400,120 366,100 366,60" />
        <polygon points="700,180 725,195 725,225 700,240 675,225 675,195" />
        <polygon points="120,420 145,435 145,465 120,480 95,465 95,435" />
      </g>
    </svg>

    <div class="hero-container">
      <!-- Right: Teacher info -->
      <div class="hero-info">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          {{ badgeText }}
        </div>

        <h1 class="hero-name" v-if="auth">أهلاً، {{ userName }} 👋</h1>
        <h1 class="hero-name" v-else>{{ nameGuest }}</h1>

        <p class="hero-subtitle">{{ subtitle }}</p>

        <p class="hero-desc" v-if="!auth">{{ descriptionGuest }}</p>
        <p class="hero-desc" v-else>
          {{ descriptionAuth }}
          <span v-if="academicYearLabel" class="level-chip">{{ academicYearLabel }}</span>
        </p>

        <!-- CTAs for guests -->
        <div class="hero-actions" v-if="!auth">
          <a :href="whatsappUrl" target="_blank" class="btn-hero-wa">
            <i class="pi pi-whatsapp"></i> {{ whatsappLabel }}
          </a>
        </div>

        <!-- CTAs for authenticated -->
        <div class="hero-actions" v-else>
          <router-link :to="ctaAuthPrimary.link" class="btn-hero-primary">{{ ctaAuthPrimary.label }}</router-link>
          <router-link :to="ctaAuthSecondary.link" class="btn-hero-secondary">{{ ctaAuthSecondary.label }}</router-link>
        </div>

        <!-- Social proof (guests only) -->
        <div class="social-proof" v-if="!auth">
          <div class="proof-avatars">
            <div class="pa pa-1">أ</div>
            <div class="pa pa-2">م</div>
            <div class="pa pa-3">س</div>
            <div class="pa pa-4">ي</div>
            <div class="pa pa-5">ف</div>
          </div>
          <span>{{ socialProofText }}</span>
        </div>
      </div>

      <!-- Left: Avatar + floating stat cards -->
      <div class="hero-visual">
        <div class="avatar-scene">
          <div class="avatar-glow"></div>
          <div class="avatar-ring">
            <img :src="teacherAvatar || logoImg" alt="أستاذ/ مصطفى حسني" />
          </div>
          <div class="orbit orbit-1"><div class="orbit-dot"></div></div>
          <div class="orbit orbit-2"><div class="orbit-dot orbit-dot-2"></div></div>
        </div>

        <div class="stat-card" :class="`stat-card-${i + 1}`" v-for="(s, i) in stats.slice(0, 3)" :key="i">
          <div class="stat-card-icon">{{ s.icon }}</div>
          <div class="stat-card-body">
            <div class="stat-card-num">{{ s.number }}</div>
            <div class="stat-card-label">{{ s.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="scroll-indicator">
      <div class="scroll-mouse"><div class="scroll-wheel"></div></div>
      <span>اكتشف أكثر</span>
    </div>
  </section>

  <!-- ══════════════════════════════════════════
       MOBILE APP HOME SCREEN  (hidden on desktop >768px)
       Feels like opening a premium learning app.
  ══════════════════════════════════════════ -->
  <section class="mah" dir="rtl">

    <!-- ── AUTH LAYOUT ── -->
    <template v-if="auth">

      <!-- Gradient app header: greeting + teacher card -->
      <div class="mah-header">
        <div class="mah-greeting">
          <div class="mah-greeting-text">
            <span class="mah-hello">مرحباً مجدداً،</span>
            <span class="mah-name">{{ userName }} 👋</span>
          </div>
          <span class="mah-level-chip" v-if="academicYearLabel">{{ academicYearLabel }}</span>
        </div>

        <!-- Compact teacher bar -->
        <div class="mah-teacher-bar">
          <div class="mah-tb-avatar">
            <img :src="teacherAvatar || faviconImg" alt="أستاذ/ مصطفى حسني" />
          </div>
          <div class="mah-tb-info">
            <div class="mah-tb-name">{{ nameGuest }}</div>
            <div class="mah-tb-sub">{{ teacherBarSubtitle }}</div>
          </div>
          <div class="mah-tb-stats">
            <template v-for="(s, i) in tbStats" :key="i">
              <div class="mah-tb-divider" v-if="i > 0"></div>
              <div class="mah-tb-stat">
                <span>{{ s.number }}</span>
                <small>{{ s.label }}</small>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Quick access grid -->
      <div class="mah-quick-section">
        <div class="mah-sec-title">وصول سريع</div>
        <div class="mah-quick-grid">
          <router-link to="/contents" class="mah-qcard mah-qc-lessons">
            <span class="mah-qc-icon">📚</span>
            <span class="mah-qc-label">محتوى الحصص</span>
            <span class="mah-qc-sub">فيديوهات وملفات</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/exams?type=online" class="mah-qcard mah-qc-exams">
            <span class="mah-qc-icon">📝</span>
            <span class="mah-qc-label">الامتحانات</span>
            <span class="mah-qc-sub">أونلاين فوري</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/exams?type=homework" class="mah-qcard mah-qc-hw">
            <span class="mah-qc-icon">🏠</span>
            <span class="mah-qc-label">الواجب المنزلي</span>
            <span class="mah-qc-sub">تدريبات يومية</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/my_exams" class="mah-qcard mah-qc-results">
            <span class="mah-qc-icon">📊</span>
            <span class="mah-qc-label">نتائجي</span>
            <span class="mah-qc-sub">تابع تقدمك</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ── GUEST LAYOUT ── -->
    <template v-else>

      <!-- Teacher hero card -->
      <div class="mah-guest-hero">
        <div class="mah-gh-blobs" aria-hidden="true">
          <div class="mah-gh-blob-1"></div>
          <div class="mah-gh-blob-2"></div>
        </div>

        <!-- Math symbols (subtle) -->
        <div class="mah-gh-math" aria-hidden="true">
          <span>∑</span><span>π</span><span>√</span><span>∫</span>
          <span>∞</span><span>θ</span><span>±</span><span>Δ</span>
        </div>

        <div class="mah-gh-inner">
          <div class="mah-gh-avatar">
            <img :src="teacherAvatar || faviconImg" alt="أستاذ/ مصطفى حسني" />
          </div>
          <div class="mah-gh-badge">
            <span class="mah-gh-dot"></span>
            {{ badgeText }}
          </div>
          <h1 class="mah-gh-name">{{ nameGuest }}</h1>
          <p class="mah-gh-sub">{{ subtitle }}</p>
          <div class="mah-gh-stats">
            <template v-for="(s, i) in stats.slice(0, 3)" :key="i">
              <div class="mah-gh-stat-div" v-if="i > 0"></div>
              <div class="mah-gh-stat"><span>{{ s.number }}</span><small>{{ s.label }}</small></div>
            </template>
          </div>
        </div>
      </div>

      <!-- CTA actions -->
      <div class="mah-guest-cta">
        <a :href="whatsappUrl" target="_blank" class="mah-btn mah-btn-wa">
          <i class="pi pi-whatsapp"></i> {{ whatsappLabelMobile }}
        </a>
        <div class="mah-social-proof">
          <div class="mah-sp-avatars">
            <div class="mah-sp-av mah-sp-1">أ</div>
            <div class="mah-sp-av mah-sp-2">م</div>
            <div class="mah-sp-av mah-sp-3">س</div>
          </div>
          <span>{{ socialProofText }}</span>
        </div>
      </div>

      <!-- Features preview grid -->
      <div class="mah-features">
        <div class="mah-sec-title">لماذا منصتنا؟</div>
        <div class="mah-feat-grid">
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-purple">🎬</span>
            <div>
              <div class="mah-feat-label">دروس فيديو</div>
              <div class="mah-feat-desc">شرح واضح بجودة عالية</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-blue">📝</span>
            <div>
              <div class="mah-feat-label">امتحانات تفاعلية</div>
              <div class="mah-feat-desc">تصحيح فوري ومراجعة</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-green">📊</span>
            <div>
              <div class="mah-feat-label">متابعة التقدم</div>
              <div class="mah-feat-desc">تتبع أداءك ونتائجك</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-orange">💬</span>
            <div>
              <div class="mah-feat-label">دعم مستمر</div>
              <div class="mah-feat-desc">تواصل مباشر مع الأستاذ</div>
            </div>
          </div>
        </div>
      </div>

    </template>

  </section>

</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useAcademicYears } from '@/composables/useAcademicYears';
import logoImg from '@/assets/images/logo.png';
import faviconImg from '@/assets/images/favicon.png';

const props = defineProps({
  auth: { type: Boolean, default: false },
  user: { type: Object, default: null },
  config: { type: Object, default: () => ({}) },
});

const { getAcademicYearName, fetchAcademicYears } = useAcademicYears();
onMounted(fetchAcademicYears);

const userName = computed(() => props.user?.name?.split(' ')[0] || '');
const academicYearLabel = computed(() => getAcademicYearName(props.user?.academic_year_id));

const badgeText            = computed(() => props.config.badge_text ?? '🎓 منصة الكيمياء الأولى');
const nameGuest             = computed(() => props.config.name_guest ?? 'أستاذ/ مصطفى حسني');
const subtitle              = computed(() => props.config.subtitle ?? 'أستاذ الكيمياء للمرحلة الثانوية');
const teacherBarSubtitle    = computed(() => props.config.teacher_bar_subtitle ?? 'أستاذ الكيمياء');
const descriptionGuest      = computed(() => props.config.description_guest ?? 'انضم إلى آلاف الطلاب الذين يتفوقون في الكيمياء مع منهج متطور، شرح مبسط، وامتحانات تفاعلية.');
const descriptionAuth       = computed(() => props.config.description_auth ?? 'أكمل رحلتك التعليمية — دروسك ونتائجك في انتظارك');
const whatsappUrl           = computed(() => props.config.whatsapp_url ?? 'https://wa.me/+201008430711');
const whatsappLabel         = computed(() => props.config.whatsapp_label ?? 'تواصل معنا');
const whatsappLabelMobile   = computed(() => props.config.whatsapp_label_mobile ?? 'تواصل مع الأستاذ');
const ctaAuthPrimary        = computed(() => props.config.cta_auth_primary ?? { label: '📚 محتوى الحصص', link: '/contents' });
const ctaAuthSecondary      = computed(() => props.config.cta_auth_secondary ?? { label: '📝 الامتحانات', link: '/exams?type=online' });
const socialProofText       = computed(() => props.config.social_proof_text ?? '+2000 طالب يتعلمون الآن');
const teacherAvatar         = computed(() => props.config.teacher_avatar || null);
const stats = computed(() => (Array.isArray(props.config.stats) && props.config.stats.length
  ? props.config.stats
  : [
    { icon: '👨‍🎓', number: '2000+', label: 'طالب متميز' },
    { icon: '📚', number: '350+', label: 'درس مسجّل' },
    { icon: '🏆', number: '95%', label: 'نسبة النجاح' },
  ]));
const tbStats = computed(() => [stats.value[0], stats.value[2]].filter(Boolean));
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ══════════════════════════════════════════
   DESKTOP HERO  (all original styles preserved)
══════════════════════════════════════════ */

/* ── Shared buttons ── */
.btn-hero-primary, .btn-hero-secondary, .btn-hero-wa {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem;
  padding: .9rem 2rem; border-radius: 99px;
  font-weight: 700; font-size: 1rem; text-decoration: none;
  transition: all .3s cubic-bezier(.4,0,.2,1); border: none; cursor: pointer;
  font-family: 'Cairo', sans-serif;
}
.btn-hero-primary  { background: linear-gradient(135deg,#051629,#F7B424); color:#fff; box-shadow:0 12px 32px rgba(5, 22, 41,.3); }
.btn-hero-primary:hover  { transform:translateY(-3px); box-shadow:0 20px 50px rgba(5, 22, 41,.45); color:#fff; }
.btn-hero-secondary { background:transparent; color:#051629; border:2px solid rgba(5, 22, 41,.25) !important; }
.btn-hero-secondary:hover { background:rgba(5, 22, 41,.06); border-color:#051629 !important; transform:translateY(-2px); color:#051629; }
.btn-hero-wa { background:linear-gradient(135deg,#25d366,#128C7E); color:#fff; box-shadow:0 12px 30px rgba(37,211,102,.25); }
.btn-hero-wa:hover { transform:translateY(-3px); box-shadow:0 20px 45px rgba(37,211,102,.4); color:#fff; }

/* ── Hero ── */
.hero {
  min-height: 100vh;
  background: linear-gradient(135deg,#051629 0%,#0a2038 45%,#02090f 100%);
  position: relative; overflow: hidden;
  display: flex; flex-direction: column; align-items: stretch;
  font-family: 'Cairo', sans-serif; direction: rtl;
}

/* Blobs */
.hero-bg { position:absolute; inset:0; pointer-events:none; }
.blob { position:absolute; border-radius:50%; filter:blur(80px); opacity:.35; animation:blobMove 8s ease-in-out infinite; }
.blob-1 { width:600px;height:600px; background:radial-gradient(circle,#051629,#F7B424); top:-200px; right:-100px; }
.blob-2 { width:500px;height:500px; background:radial-gradient(circle,#F7B424,#0066ff); bottom:-150px;left:-100px; animation-delay:-3s; }
.blob-3 { width:400px;height:400px; background:radial-gradient(circle,#00D4A6,#F7B424); top:40%;left:35%; animation-delay:-5s; }
.blob-4 { width:300px;height:300px; background:radial-gradient(circle,#F7B424,#FBCE72); top:60%;right:20%; animation-delay:-2s; }
@keyframes blobMove { 0%,100%{transform:scale(1) translate(0,0)} 33%{transform:scale(1.1) translate(30px,-30px)} 66%{transform:scale(.9) translate(-20px,20px)} }

/* Math symbols */
.math-symbols { position:absolute;inset:0;pointer-events:none;overflow:hidden; }
.ms { position:absolute;color:rgba(255,255,255,.08);font-size:3rem;font-family:'Georgia',serif;font-weight:700;animation:mathFloat 6s ease-in-out infinite;user-select:none; }
.ms-1  { top:10%;right:8%;font-size:4rem;animation-duration:7s; }
.ms-2  { top:25%;right:25%;font-size:2.5rem;animation-delay:-1s;animation-duration:8s; }
.ms-3  { top:60%;right:15%;font-size:3.5rem;animation-delay:-2s;animation-duration:6s; }
.ms-4  { top:15%;left:12%;font-size:3rem;animation-delay:-3s;animation-duration:9s; }
.ms-5  { top:50%;left:8%;font-size:4.5rem;animation-delay:-1.5s;animation-duration:7s; }
.ms-6  { top:75%;left:20%;font-size:2.8rem;animation-delay:-4s;animation-duration:8s; }
.ms-7  { top:35%;left:40%;font-size:2rem;animation-delay:-2.5s;animation-duration:10s; }
.ms-8  { top:80%;right:35%;font-size:3rem;animation-delay:-.5s;animation-duration:7s; }
.ms-9  { top:5%;left:50%;font-size:2.5rem;animation-delay:-3.5s;animation-duration:9s; }
.ms-10 { top:90%;right:55%;font-size:2rem;animation-delay:-1s;animation-duration:8s; }
.ms-11 { top:40%;right:5%;font-size:2.5rem;animation-delay:-4.5s;animation-duration:6s; }
.ms-12 { top:65%;left:45%;font-size:3rem;animation-delay:-2s;animation-duration:11s; }
.ms-13 { top:20%;left:30%;font-size:2rem;animation-delay:-3s;animation-duration:8s; }
.ms-14 { top:85%;left:65%;font-size:3.5rem;animation-duration:7s; }
@keyframes mathFloat { 0%,100%{transform:translate(0,0) rotate(0deg);opacity:.08} 25%{transform:translate(15px,-20px) rotate(8deg);opacity:.14} 75%{transform:translate(-10px,15px) rotate(-6deg);opacity:.06} }

/* Molecular / hexagon decoration — subtle brand texture, never competes with content */
.molecule-pattern { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; opacity:.07; z-index:0; }

/* Hero layout */
.hero-container {
  max-width:1200px;margin:0 auto;width:100%;
  padding:120px 2rem 80px;
  display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;
  position:relative;z-index:1;flex:1;
}
.hero-info { display:flex;flex-direction:column;gap:1.5rem; }

.hero-badge {
  display:inline-flex;align-items:center;gap:.5rem;
  background:rgba(255,255,255,.1);backdrop-filter:blur(10px);
  border:1px solid rgba(255,255,255,.2);border-radius:99px;
  padding:.4rem 1.1rem;color:rgba(255,255,255,.9);font-size:.85rem;font-weight:600;
  width:fit-content;animation:fadeSlideIn .8s ease both;
}
.badge-dot { width:8px;height:8px;border-radius:50%;background:#00D4A6;box-shadow:0 0 8px #00D4A6;animation:pulseGreen 1.5s infinite; }
@keyframes pulseGreen { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:.6} }

.hero-name { font-size:clamp(2.2rem,5vw,3.5rem);font-weight:900;color:#fff;line-height:1.15;letter-spacing:-.02em;animation:fadeSlideIn .8s ease .1s both; }
.hero-subtitle { font-size:1.1rem;color:rgba(255,255,255,.7);font-weight:500;animation:fadeSlideIn .8s ease .2s both; }
.hero-desc { font-size:1rem;color:rgba(255,255,255,.6);line-height:1.8;max-width:480px;animation:fadeSlideIn .8s ease .3s both; }
.level-chip { display:inline-block;background:rgba(0,212,166,.15);color:#00D4A6;border:1px solid rgba(0,212,166,.3);border-radius:99px;padding:.15rem .75rem;font-size:.82rem;font-weight:700;margin-right:.5rem; }
.hero-actions { display:flex;gap:1rem;flex-wrap:wrap;animation:fadeSlideIn .8s ease .4s both; }
.hero-actions .btn-hero-primary, .hero-actions .btn-hero-secondary, .hero-actions .btn-hero-wa { width:auto; }

.social-proof { display:flex;align-items:center;gap:.75rem;animation:fadeSlideIn .8s ease .5s both;color:rgba(255,255,255,.6);font-size:.88rem; }
.proof-avatars { display:flex; }
.pa { width:34px;height:34px;border-radius:50%;border:2px solid rgba(255,255,255,.3);display:flex;align-items:center;justify-content:center;font-size:.85rem;font-weight:700;color:#fff;margin-left:-8px; }
.pa:first-child { margin-left:0; }
.pa-1 { background:linear-gradient(135deg,#051629,#F7B424); }
.pa-2 { background:linear-gradient(135deg,#F7B424,#0066ff); }
.pa-3 { background:linear-gradient(135deg,#00D4A6,#F7B424); }
.pa-4 { background:linear-gradient(135deg,#F7B424,#FBCE72); }
.pa-5 { background:linear-gradient(135deg,#f59e0b,#ef4444); }

/* Hero visual */
.hero-visual { position:relative;display:flex;align-items:center;justify-content:center;height:500px;animation:fadeSlideIn .8s ease .2s both; }
.avatar-scene { position:relative;display:flex;align-items:center;justify-content:center; }
.avatar-glow { position:absolute;width:280px;height:280px;border-radius:50%;background:radial-gradient(circle,rgba(5, 22, 41,.5) 0%,transparent 70%);animation:glowPulse 3s ease-in-out infinite; }
@keyframes glowPulse { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.2);opacity:.8} }
.avatar-ring { width:200px;height:200px;border-radius:50%;padding:5px;background:linear-gradient(135deg,#051629,#F7B424,#F7B424);box-shadow:0 0 60px rgba(5, 22, 41,.5),0 0 120px rgba(5, 22, 41,.2);animation:floatY 4s ease-in-out infinite;position:relative;z-index:1;display:flex;align-items:center;justify-content:center; }
.avatar-ring img { width:100%;height:100%;border-radius:50%;border:4px solid #fff;object-fit:cover; }
.orbit { position:absolute;border-radius:50%;border:1px solid rgba(255,255,255,.15);display:flex;align-items:flex-start;justify-content:flex-end; }
.orbit-1 { width:280px;height:280px;animation:orbitSpin 8s linear infinite; }
.orbit-2 { width:350px;height:350px;animation:orbitSpin 12s linear infinite reverse; }
.orbit-dot { width:12px;height:12px;border-radius:50%;background:linear-gradient(135deg,#051629,#F7B424);box-shadow:0 0 12px rgba(5, 22, 41,.8);margin:-6px;transform:translateY(6px); }
.orbit-dot-2 { background:linear-gradient(135deg,#00D4A6,#F7B424);box-shadow:0 0 12px rgba(0,212,166,.8); }
@keyframes orbitSpin { to { transform:rotate(360deg); } }

.stat-card { position:absolute;background:rgba(255,255,255,.1);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,.2);border-radius:20px;padding:1rem 1.25rem;display:flex;align-items:center;gap:.85rem;min-width:150px;box-shadow:0 16px 40px rgba(0,0,0,.2);transition:transform .3s;cursor:default; }
.stat-card:hover { transform:scale(1.05); }
.stat-card-icon { font-size:1.8rem; }
.stat-card-num { font-size:1.4rem;font-weight:800;color:#fff;line-height:1; }
.stat-card-label { font-size:.75rem;color:rgba(255,255,255,.7);margin-top:.1rem; }
.stat-card-1 { top:5%;right:-5%;animation:floatCard1 4s ease-in-out infinite; }
.stat-card-2 { top:45%;left:-5%;animation:floatCard2 5s ease-in-out infinite; }
.stat-card-3 { bottom:8%;right:5%;animation:floatCard3 3.5s ease-in-out infinite; }
@keyframes floatCard1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-8px,-12px)} }
@keyframes floatCard2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,8px)} }
@keyframes floatCard3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-6px,-10px)} }

/* Scroll indicator */
.scroll-indicator { position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:.5rem;color:rgba(255,255,255,.5);font-size:.8rem;z-index:1;animation:fadeIn 1s ease 1s both; }
.scroll-mouse { width:24px;height:38px;border:2px solid rgba(255,255,255,.4);border-radius:12px;display:flex;justify-content:center;padding-top:5px; }
.scroll-wheel { width:4px;height:8px;background:rgba(255,255,255,.6);border-radius:2px;animation:scrollWheel 1.5s ease infinite; }
@keyframes scrollWheel { 0%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(12px)} }

/* Animations */
@keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes fadeSlideIn { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes fadeIn { from{opacity:0} to{opacity:1} }

/* Desktop responsive (900px tablet) */
@media(max-width:900px) {
  .hero-container { grid-template-columns:1fr;padding:100px 1.5rem 60px;gap:2rem; }
  .hero-visual { height:320px;order:-1; }
  .avatar-ring { width:150px;height:150px; }
  .orbit-1 { width:210px;height:210px; }
  .orbit-2 { width:260px;height:260px; }
  .stat-card-1 { top:0;right:0; }
  .stat-card-2 { display:none; }
  .stat-card-3 { bottom:0;right:0; }
  .hero-name { font-size:2.2rem; }
  .hero-actions { flex-direction:column; }
  .hero-actions .btn-hero-primary, .hero-actions .btn-hero-wa { width:100%; }
}
@media(max-width:600px) { .hero-container { padding:80px 1rem 50px; } }

/* ── Show/hide control ── */
.mah { display: none; }

@media (max-width: 768px) {
  .hero { display: none; }
  .mah  { display: flex; }
}

/* ══════════════════════════════════════════
   MOBILE APP HOME SCREEN  (.mah)
   Everything below is mobile-only.
══════════════════════════════════════════ */
.mah {
  flex-direction: column;
  background: #F9F9F9;
  font-family: 'Cairo', 'Almarai', sans-serif;
  direction: rtl;
  overflow-x: hidden;
  animation: mahFadeIn .35s ease both;
}
@keyframes mahFadeIn { from { opacity: 0; } to { opacity: 1; } }

/* ── Gradient app header (auth): greeting + teacher card ── */
.mah-header {
  display: flex;
  flex-direction: column;
  gap: .85rem;
  padding: .85rem 1rem 1.1rem;
  background: linear-gradient(160deg, #051629 0%, #3059A2 55%, #F7B424 100%);
  border-radius: 0 0 24px 24px;
  box-shadow: 0 10px 30px rgba(5, 22, 41,.22);
}

/* ── Greeting row (auth) ── */
.mah-greeting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 .15rem;
  gap: .75rem;
  min-width: 0;
}
.mah-greeting-text {
  display: flex;
  flex-direction: column;
  gap: .1rem;
  min-width: 0;
}
.mah-hello {
  font-size: .76rem;
  color: rgba(255,255,255,.75);
  font-weight: 500;
  line-height: 1.4;
}
.mah-name {
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mah-level-chip {
  background: rgba(255,255,255,.16);
  border: 1px solid rgba(255,255,255,.28);
  color: #fff;
  border-radius: 99px;
  padding: .28rem .9rem;
  font-size: .75rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Teacher bar (auth — glass card inside header) ── */
.mah-teacher-bar {
  display: flex;
  align-items: center;
  gap: .8rem;
  padding: .75rem .9rem;
  background: rgba(255,255,255,.13);
  border: 1px solid rgba(255,255,255,.2);
  border-radius: 20px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  min-width: 0;
}
.mah-tb-avatar {
  width: clamp(42px, 12vw, 52px);
  height: clamp(42px, 12vw, 52px);
  border-radius: 50%;
  padding: 2.5px;
  background: linear-gradient(135deg, rgba(255,255,255,.85), rgba(255,255,255,.4));
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.mah-tb-avatar img {
  width: 100%; height: 100%;
  border-radius: 50%;
  border: 2px solid #fff;
  object-fit: cover;
}
.mah-tb-info {
  flex: 1;
  min-width: 0;
}
.mah-tb-name {
  font-size: .95rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mah-tb-sub {
  font-size: .72rem;
  color: rgba(255,255,255,.72);
  line-height: 1.4;
  margin-top: .1rem;
}
.mah-tb-stats {
  display: flex;
  align-items: center;
  gap: .6rem;
  flex-shrink: 0;
}
.mah-tb-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .05rem;
}
.mah-tb-stat span {
  font-size: .92rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}
.mah-tb-stat small {
  font-size: .6rem;
  color: rgba(255,255,255,.6);
  font-weight: 500;
}
.mah-tb-divider {
  width: 1px;
  height: 28px;
  background: rgba(255,255,255,.18);
}

/* ── Quick access section (auth) ── */
.mah-quick-section {
  padding: 1.15rem 1rem 1rem;
  background: #F9F9F9;
}
.mah-sec-title {
  font-size: .75rem;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: .65rem;
  padding: 0 .2rem;
}
.mah-quick-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .6rem;
}
.mah-qcard {
  border-radius: 20px;
  padding: 1.05rem .9rem;
  display: flex;
  flex-direction: column;
  gap: .35rem;
  text-decoration: none;
  color: #fff;
  position: relative;
  overflow: hidden;
  min-height: 110px;
  transition: transform .18s ease, box-shadow .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-qcard:active { transform: scale(0.95); }
.mah-qc-lessons { background: linear-gradient(145deg, #051629 0%, #F7B424 100%); box-shadow: 0 8px 24px rgba(5, 22, 41,.28); }
.mah-qc-exams   { background: linear-gradient(145deg, #F7B424 0%, #0066ff 100%); box-shadow: 0 8px 24px rgba(0,102,255,.22); }
.mah-qc-hw      { background: linear-gradient(145deg, #00D4A6 0%, #00B4D8 100%); box-shadow: 0 8px 24px rgba(0,212,166,.22); }
.mah-qc-results { background: linear-gradient(145deg, #f59e0b 0%, #ef4444 100%); box-shadow: 0 8px 24px rgba(245,158,11,.22); }
.mah-qc-icon  { font-size: 1.65rem; line-height: 1; }
.mah-qc-label { font-size: .88rem; font-weight: 800; line-height: 1.25; }
.mah-qc-sub   { font-size: .7rem; opacity: .82; line-height: 1.4; }
.mah-qc-arrow { font-size: .85rem; opacity: .65; margin-top: auto; align-self: flex-end; transition: transform .18s ease; }
.mah-qcard:active .mah-qc-arrow { transform: translateX(-4px); }

/* ── Guest hero card ── */
.mah-guest-hero {
  background: linear-gradient(160deg, #051629 0%, #3059A2 55%, #F7B424 100%);
  position: relative;
  overflow: hidden;
  padding: 1.6rem 1.25rem 1.5rem;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 10px 30px rgba(5, 22, 41,.22);
}
.mah-gh-blobs { position: absolute; inset: 0; pointer-events: none; }
.mah-gh-blob-1 {
  position: absolute; width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(255,255,255,.22), transparent);
  top: -80px; right: -60px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-blob-2 {
  position: absolute; width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(0,212,166,.3), transparent);
  bottom: -60px; left: -40px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-math {
  position: absolute; inset: 0; overflow: hidden; pointer-events: none;
  display: flex; flex-wrap: wrap;
}
.mah-gh-math span {
  position: absolute;
  font-family: 'Georgia', serif;
  font-size: 2rem;
  color: rgba(255,255,255,.06);
  font-weight: 700;
  user-select: none;
}
.mah-gh-math span:nth-child(1) { top: 8%;  right: 6%; font-size: 2.5rem; }
.mah-gh-math span:nth-child(2) { top: 20%; left: 8%;  font-size: 1.8rem; }
.mah-gh-math span:nth-child(3) { top: 55%; right: 12%; font-size: 2rem; }
.mah-gh-math span:nth-child(4) { top: 65%; left: 5%;  font-size: 2.8rem; }
.mah-gh-math span:nth-child(5) { top: 10%; left: 40%; font-size: 3rem; }
.mah-gh-math span:nth-child(6) { bottom: 15%; right: 35%; font-size: 1.6rem; }
.mah-gh-math span:nth-child(7) { bottom: 8%;  left: 30%; font-size: 2.2rem; }
.mah-gh-math span:nth-child(8) { top: 40%; right: 30%; font-size: 1.5rem; }

.mah-gh-inner {
  position: relative; z-index: 1;
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: .6rem;
}
.mah-gh-avatar {
  width: clamp(60px, 18vw, 72px);
  height: clamp(60px, 18vw, 72px);
  border-radius: 50%;
  padding: 3px;
  background: linear-gradient(135deg, rgba(255,255,255,.9), rgba(255,255,255,.35));
  box-shadow: 0 12px 32px rgba(0,0,0,.22);
  display: flex; align-items: center; justify-content: center;
  animation: mahFloat 3.5s ease-in-out infinite;
}
.mah-gh-avatar img {
  width: 100%; height: 100%;
  border-radius: 50%;
  border: 3px solid #fff;
  object-fit: cover;
}
@keyframes mahFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }

.mah-gh-badge {
  display: inline-flex; align-items: center; gap: .4rem;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
  border-radius: 99px; padding: .28rem .9rem;
  font-size: .75rem; font-weight: 600; color: rgba(255,255,255,.9);
}
.mah-gh-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #00D4A6; box-shadow: 0 0 6px #00D4A6;
  animation: ghDotPulse 1.5s infinite;
}
@keyframes ghDotPulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.6} }

.mah-gh-name {
  font-size: 1.7rem; font-weight: 900; color: #fff;
  line-height: 1.3; letter-spacing: -.01em;
}
.mah-gh-sub {
  font-size: .78rem; color: rgba(255,255,255,.8);
  line-height: 1.5;
  background: rgba(255,255,255,.12); padding: .3rem 1rem;
  border-radius: 99px; backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,.18);
}
.mah-gh-stats {
  display: flex; align-items: center; gap: .85rem;
  padding: .6rem 1.35rem;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2);
  border-radius: 16px; backdrop-filter: blur(12px);
  margin-top: .2rem;
}
.mah-gh-stat {
  display: flex; flex-direction: column; align-items: center; gap: .05rem;
}
.mah-gh-stat span {
  font-size: 1.15rem; font-weight: 900; color: #fff; line-height: 1;
}
.mah-gh-stat small {
  font-size: .65rem; color: rgba(255,255,255,.6); font-weight: 500;
}
.mah-gh-stat-div {
  width: 1px; height: 30px; background: rgba(255,255,255,.18);
}

/* ── Guest CTA section ── */
.mah-guest-cta {
  padding: 1.25rem 1rem 1rem;
  background: transparent;
  display: flex; flex-direction: column; gap: .7rem;
}
.mah-btn {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  padding: .9rem 1.5rem;
  border-radius: 99px;
  font-weight: 700; font-size: .97rem;
  text-decoration: none; color: #fff;
  font-family: 'Cairo', 'Almarai', sans-serif;
  width: 100%; border: none; cursor: pointer;
  transition: opacity .15s ease, transform .15s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-btn:active { transform: scale(0.97); opacity: .92; color: #fff; }
.mah-btn-primary { background: linear-gradient(135deg, #051629, #F7B424); box-shadow: 0 12px 32px rgba(5, 22, 41,.3); }
.mah-btn-wa { background: linear-gradient(135deg, #25d366, #128C7E); box-shadow: 0 12px 30px rgba(37,211,102,.25); }

.mah-social-proof {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  font-size: .8rem; color: #6B7280; padding-top: .1rem;
}
.mah-sp-avatars { display: flex; }
.mah-sp-av {
  width: 26px; height: 26px;
  border-radius: 50%; border: 2px solid #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: .72rem; font-weight: 700; color: #fff;
  margin-left: -5px;
}
.mah-sp-av:first-child { margin-left: 0; }
.mah-sp-1 { background: linear-gradient(135deg, #051629, #F7B424); }
.mah-sp-2 { background: linear-gradient(135deg, #F7B424, #0066ff); }
.mah-sp-3 { background: linear-gradient(135deg, #00D4A6, #F7B424); }

/* ── Features grid (guest) ── */
.mah-features {
  padding: 1.15rem 1rem 1rem;
  background: #F9F9F9;
}
.mah-feat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .6rem;
}
.mah-feat-card {
  background: #fff;
  border-radius: 20px;
  padding: .95rem .9rem;
  display: flex;
  align-items: flex-start;
  gap: .65rem;
  border: 1px solid rgba(5, 22, 41,.06);
  box-shadow: 0 2px 10px rgba(0,0,0,.05);
  transition: transform .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-feat-card:active { transform: scale(0.97); }
.mah-fi {
  width: 38px; height: 38px;
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}
.mah-fi-purple { background: linear-gradient(135deg, rgba(5, 22, 41,.1),  rgba(247, 180, 36,.1)); }
.mah-fi-blue   { background: linear-gradient(135deg, rgba(0,194,255,.1),   rgba(0,102,255,.1)); }
.mah-fi-green  { background: linear-gradient(135deg, rgba(0,212,166,.1),   rgba(0,194,255,.1)); }
.mah-fi-orange { background: linear-gradient(135deg, rgba(245,158,11,.1),  rgba(239,68,68,.1)); }
.mah-feat-label { font-size: .83rem; font-weight: 700; color: #1F2937; margin-bottom: .15rem; }
.mah-feat-desc  { font-size: .7rem; color: #6B7280; line-height: 1.5; }
</style>
